/**
 * Composant Dossier Final RealEase (onglet de la fiche projet)
 *
 * Permet a l'equipe de :
 * - Configurer la checklist (sections + phases) du dossier final
 * - Synchroniser les fichiers Drive presents dans chaque sous-dossier
 * - Generer un PDF a la volee (preview / download)
 * - Pousser le PDF au portail client + email
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FinalDossierTab = ({ projectId, projectName, projectStatus }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [driveIndex, setDriveIndex] = useState(null);
  const [error, setError] = useState('');

  const fetchCfg = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/final-dossier`);
      setCfg(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const save = async (newCfg = cfg) => {
    if (!newCfg) return;
    setSaving(true); setError('');
    try {
      await axios.put(`${API}/projects/${projectId}/final-dossier`, {
        sections: newCfg.sections,
        phases: newCfg.phases,
        google_review_url: newCfg.google_review_url,
        trustpilot_url: newCfg.trustpilot_url,
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally { setSaving(false); }
  };

  const toggleSection = (idx) => {
    const next = { ...cfg, sections: cfg.sections.map((s, i) => i === idx ? { ...s, included: !s.included } : s) };
    setCfg(next); save(next);
  };
  const updateSection = (idx, field, value) => {
    const next = { ...cfg, sections: cfg.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s) };
    setCfg(next);
  };
  const removeSection = (idx) => {
    if (!window.confirm('Retirer cette section du dossier final ?')) return;
    const next = { ...cfg, sections: cfg.sections.filter((_, i) => i !== idx) };
    setCfg(next); save(next);
  };
  const addCustomSection = () => {
    const label = window.prompt('Nom de la section (ex: "Plans architecte") :');
    if (!label) return;
    const folder = window.prompt('Nom exact du sous-dossier Drive (laisser vide si pas de fichiers a integrer) :', label);
    const key = `custom_${Date.now()}`;
    const next = {
      ...cfg, sections: [...cfg.sections, {
        key, label, type: folder ? 'media' : 'auto', included: true, drive_folder: folder || null,
      }]
    };
    setCfg(next); save(next);
  };

  const togglePhase = (idx) => {
    const next = { ...cfg, phases: cfg.phases.map((p, i) => i === idx ? { ...p, included: !p.included } : p) };
    setCfg(next); save(next);
  };
  const updatePhase = (idx, field, value) => {
    const next = { ...cfg, phases: cfg.phases.map((p, i) => i === idx ? { ...p, [field]: value } : p) };
    setCfg(next);
  };
  const removePhase = (idx) => {
    const next = { ...cfg, phases: cfg.phases.filter((_, i) => i !== idx) };
    setCfg(next); save(next);
  };
  const addCustomPhase = () => {
    const label = window.prompt('Nom de la phase (ex: "Pose cuisine equipee") :');
    if (!label) return;
    const next = {
      ...cfg, phases: [...cfg.phases, {
        key: `custom_${Date.now()}`, label, status: 'pending', date: '', note: '', included: true
      }]
    };
    setCfg(next); save(next);
  };

  const syncDrive = async () => {
    setSaving(true); setError('');
    try {
      const r = await axios.post(`${API}/projects/${projectId}/final-dossier/sync-drive`);
      setDriveIndex(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur sync Drive');
    } finally { setSaving(false); }
  };

  const generate = async () => {
    setGenerating(true); setError('');
    try {
      const r = await axios.get(`${API}/projects/${projectId}/final-dossier/pdf`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      fetchCfg();
    } catch (e) {
      setError('Erreur generation PDF : ' + (e.response?.data?.detail || e.message));
    } finally { setGenerating(false); }
  };

  const sendToClient = async () => {
    if (!window.confirm('Envoyer le dossier final au client par email + portail ?\n\nLe client recevra un email avec le lien vers son espace.')) return;
    setSending(true); setError('');
    try {
      const r = await axios.post(`${API}/projects/${projectId}/final-dossier/send-to-client`);
      alert(r.data?.email_sent
        ? `Dossier envoye au client (${(r.data.size / 1024).toFixed(0)} KB).\nEmail confirmation envoyee.`
        : `Dossier stocke (${(r.data.size / 1024).toFixed(0)} KB) mais l'email n'a pas pu etre envoye (verifier email client).`);
      fetchCfg();
    } catch (e) {
      setError('Erreur envoi : ' + (e.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  if (loading) return <p className="text-gray-400 italic p-6">Chargement...</p>;
  if (!cfg) return <p className="text-red-500 p-6">Erreur : configuration introuvable.</p>;

  return (
    <div className="space-y-6" data-testid="final-dossier-tab">
      {/* Header + actions */}
      <div className="realease-card p-5 bg-gradient-to-br from-[#FDF8F0] to-white border border-[#E8DCC4]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <svg className="w-6 h-6 text-[#A57F4F]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              Dossier final RealEase
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Le livrable client en fin de projet (couverture, chiffres, phases, factures, decennales, photos avant/apres, garanties, avis).
              {projectStatus && projectStatus !== 'termine' && (
                <span className="block mt-1 text-xs text-amber-700">⚠ Le projet n'est pas encore en statut "termine". Vous pouvez tester l'apercu maintenant.</span>
              )}
            </p>
            {cfg.last_generated_at && (
              <p className="text-xs text-gray-500 mt-2">
                Dernier apercu : {new Date(cfg.last_generated_at).toLocaleString('fr-FR')}
                {cfg.last_pdf_size && ` (${(cfg.last_pdf_size / 1024).toFixed(0)} KB)`}
              </p>
            )}
            {cfg.client_sent_at && (
              <p className="text-xs text-emerald-700 font-semibold mt-1">
                ✓ Envoye au client le {new Date(cfg.client_sent_at).toLocaleString('fr-FR')}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2 min-w-[200px]">
            <button onClick={generate} disabled={generating}
                    className="px-4 py-2.5 bg-[#008A8D] text-white text-sm font-bold rounded-lg hover:bg-[#006B6E] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="finaldoc-generate-btn">
              {generating ? 'Generation...' : '⚡ Apercu / Telecharger'}
            </button>
            <button onClick={sendToClient} disabled={sending}
                    className="px-4 py-2.5 bg-[#A57F4F] text-white text-sm font-bold rounded-lg hover:bg-[#8C6A40] disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="finaldoc-send-client-btn">
              {sending ? 'Envoi...' : '✉ Envoyer au client'}
            </button>
            <button onClick={syncDrive} disabled={saving}
                    className="px-4 py-2 bg-white border border-gray-300 text-sm font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    data-testid="finaldoc-sync-drive-btn">
              ↻ Sync Drive
            </button>
          </div>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      {/* Drive index summary */}
      {driveIndex && (
        <div className="realease-card p-4 bg-blue-50 border border-blue-200">
          <p className="text-sm font-bold text-blue-900 mb-2">Synthese Drive (sections media uniquement)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(driveIndex).map(([key, info]) => (
              <div key={key} className="flex items-center justify-between p-2 bg-white rounded text-xs">
                <span className="font-semibold text-gray-700">{info.label}</span>
                <span className={`px-2 py-0.5 rounded-full font-semibold ${info.count > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {info.count} fichier{info.count > 1 ? 's' : ''} · {(info.size_total / 1024 / 1024).toFixed(1)} MB
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sections checklist */}
      <div className="realease-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900">Sections du dossier ({cfg.sections.filter(s => s.included).length}/{cfg.sections.length})</h3>
          <button onClick={addCustomSection} className="text-xs font-bold text-[#008A8D] hover:underline">+ Ajouter une section</button>
        </div>
        <div className="space-y-2">
          {cfg.sections.map((sec, idx) => (
            <div key={sec.key}
                 className={`p-3 rounded-lg border ${sec.included ? 'border-[#008A8D]/30 bg-[#008A8D]/5' : 'border-gray-200 bg-white opacity-60'}`}
                 data-testid={`finaldoc-section-${sec.key}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={!!sec.included} onChange={() => toggleSection(idx)}
                       className="w-4 h-4 mt-0.5 accent-[#008A8D] flex-shrink-0"
                       data-testid={`finaldoc-section-${sec.key}-toggle`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-gray-900">{sec.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${sec.type === 'media' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                      {sec.type === 'media' ? 'Drive' : 'Auto'}
                    </span>
                  </div>
                  {sec.type === 'media' && (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-xs text-gray-500">Sous-dossier Drive :</span>
                      <input type="text" value={sec.drive_folder || ''}
                             onChange={e => updateSection(idx, 'drive_folder', e.target.value)}
                             onBlur={() => save()}
                             className="text-xs px-2 py-0.5 border border-gray-200 rounded font-mono"
                             placeholder="ex: Photos avant"
                             data-testid={`finaldoc-section-${sec.key}-folder`} />
                    </div>
                  )}
                  {sec.notes && <p className="text-[11px] text-gray-500 mt-1 italic">{sec.notes}</p>}
                </div>
                {sec.key.startsWith('custom_') && (
                  <button onClick={() => removeSection(idx)} className="text-xs text-red-500 hover:underline">Supprimer</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Phases editable */}
      <div className="realease-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900">Phases du projet ({cfg.phases.filter(p => p.included).length}/{cfg.phases.length})</h3>
          <button onClick={addCustomPhase} className="text-xs font-bold text-[#008A8D] hover:underline">+ Ajouter une phase</button>
        </div>
        <div className="space-y-2">
          {cfg.phases.map((p, idx) => (
            <div key={p.key} className={`p-3 rounded-lg border ${p.included ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}`}
                 data-testid={`finaldoc-phase-${p.key}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <input type="checkbox" checked={!!p.included} onChange={() => togglePhase(idx)}
                       className="w-4 h-4 accent-[#008A8D] flex-shrink-0" />
                <input type="text" value={p.label} onChange={e => updatePhase(idx, 'label', e.target.value)}
                       onBlur={() => save()}
                       className="flex-1 min-w-[180px] text-sm font-semibold px-2 py-1 border border-transparent hover:border-gray-200 focus:border-[#008A8D] rounded" />
                <select value={p.status || 'pending'} onChange={e => { updatePhase(idx, 'status', e.target.value); }}
                        onBlur={() => save()}
                        className="text-xs px-2 py-1 border border-gray-200 rounded">
                  <option value="pending">⏳ En attente</option>
                  <option value="in_progress">🔧 En cours</option>
                  <option value="done">✅ Termine</option>
                </select>
                <input type="date" value={p.date || ''} onChange={e => updatePhase(idx, 'date', e.target.value)}
                       onBlur={() => save()}
                       className="text-xs px-2 py-1 border border-gray-200 rounded" />
                <input type="text" placeholder="Note" value={p.note || ''} onChange={e => updatePhase(idx, 'note', e.target.value)}
                       onBlur={() => save()}
                       className="text-xs px-2 py-1 border border-gray-200 rounded flex-1 min-w-[120px]" />
                {p.key.startsWith('custom_') && (
                  <button onClick={() => removePhase(idx)} className="text-xs text-red-500 hover:underline">×</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Liens avis client */}
      <div className="realease-card p-5">
        <h3 className="font-bold text-gray-900 mb-3">Liens d'avis client (page finale du PDF)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Avis Google Business</label>
            <input type="url" value={cfg.google_review_url || ''}
                   onChange={e => setCfg({ ...cfg, google_review_url: e.target.value })}
                   onBlur={() => save()}
                   className="realease-input"
                   placeholder="https://share.google/..."
                   data-testid="finaldoc-google-link" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Trustpilot</label>
            <input type="url" value={cfg.trustpilot_url || ''}
                   onChange={e => setCfg({ ...cfg, trustpilot_url: e.target.value })}
                   onBlur={() => save()}
                   className="realease-input"
                   placeholder="https://fr.trustpilot.com/evaluate/..."
                   data-testid="finaldoc-trustpilot-link" />
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">Ces liens generent automatiquement les QR codes affiches sur la page finale du dossier (Google Business + Trustpilot).</p>
      </div>

      {saving && <p className="text-xs text-gray-500 italic text-right">Sauvegarde...</p>}
    </div>
  );
};

export default FinalDossierTab;
